import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaReact, FaNodeJs, FaGitAlt, FaDocker, FaPython, FaAws } from 'react-icons/fa';
import { SiTypescript, SiMongodb, SiGraphql, SiNextdotjs } from 'react-icons/si';
import { TbBrandCpp } from 'react-icons/tb';

const AboutSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(180deg, #0a192f 0%, #112240 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, #64ffda, transparent, #64ffda);
    animation: glowLine 3s ease-in-out infinite;
  }

  @keyframes glowLine {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
`;

const CircuitBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.1;
  pointer-events: none;
  background: 
    linear-gradient(90deg, transparent 49%, #64ffda 49%, #64ffda 51%, transparent 51%) 0 0/50px 100%,
    linear-gradient(0deg, transparent 49%, #64ffda 49%, #64ffda 51%, transparent 51%) 0 0/100% 50px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  color: #8892b0;
  text-align: center;
  margin-bottom: 60px;
  position: relative;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  color: #ccd6f6;
  margin-bottom: 30px;
  display: inline-block;
  position: relative;

  &::after {
    content: '<about>';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 1rem;
    color: #64ffda;
    font-family: monospace;
    opacity: 0.7;
  }

  span {
    color: #64ffda;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 2px;
      bottom: -5px;
      left: 0;
      background: #64ffda;
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.5s ease;
    }
  }

  &:hover span::before {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const Description = styled(motion.div)`
  font-size: 1.1rem;
  line-height: 2;
  max-width: 900px;
  margin: 0 auto 50px;
  padding: 40px 50px;
  background: rgba(10, 25, 47, 0.7);
  border-radius: 10px;
  border: 1px solid rgba(100, 255, 218, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  text-align: left;

  p {
    margin-bottom: 2rem;
    letter-spacing: 0.3px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(45deg, transparent, #64ffda, transparent);
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

const SkillsContainer = styled.div`
  position: relative;
  height: 150px;
  margin: 50px 0;
  overflow: hidden;
  perspective: 1000px;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 150px;
    height: 100%;
    z-index: 2;
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, #0a192f, transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(-90deg, #0a192f, transparent);
  }
`;

const SkillsTrack = styled.div`
  display: flex;
  width: fit-content;
  animation: scrollRight 40s linear infinite;
  transform-style: preserve-3d;

  @keyframes scrollRight {
    from {
      transform: translateX(-50%);
    }
    to {
      transform: translateX(0);
    }
  }

  &:hover {
    animation-play-state: paused;
  }
`;

const SkillIcon = styled.div`
  font-size: 3rem;
  color: #64ffda;
  margin: 0 30px;
  opacity: 0.7;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: perspective(1000px) rotateY(0deg);
  
  svg {
    margin-bottom: 10px;
    filter: drop-shadow(0 0 8px rgba(100, 255, 218, 0.3));
  }

  span {
    font-size: 0.8rem;
    color: #8892b0;
    font-family: monospace;
    opacity: 0;
    transform: translateY(10px);
    transition: all 0.3s ease;
  }

  &:hover {
    opacity: 1;
    transform: perspective(1000px) rotateY(360deg) scale(1.2);

    span {
      opacity: 1;
      transform: translateY(0);
    }

    svg {
      filter: drop-shadow(0 0 12px rgba(100, 255, 218, 0.5));
    }
  }
`;

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const skills = [
    { Icon: FaReact, name: 'React' },
    { Icon: FaNodeJs, name: 'Node.js' },
    { Icon: SiTypescript, name: 'TypeScript' },
    { Icon: SiMongodb, name: 'MongoDB' },
    { Icon: FaDocker, name: 'Docker' },
    { Icon: FaAws, name: 'AWS' },
    { Icon: SiGraphql, name: 'GraphQL' },
    { Icon: SiNextdotjs, name: 'Next.js' },
    { Icon: FaPython, name: 'Python' },
    { Icon: FaGitAlt, name: 'Git' },
    { Icon: TbBrandCpp, name: 'C++' },
  ];

  // Double the skills array for seamless infinite scroll
  const doubledSkills = [...skills, ...skills, ...skills];

  return (
    <AboutSection id="about" ref={ref}>
      <CircuitBackground />
      <Container>
        <ContentWrapper>
          <Title
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            À propos de <span>moi</span>
          </Title>
          
          <Description
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <p>J'ai d'abord orienté mes études et ma carrière vers la gastronomie et la vente, porté par mon amour pour la cuisine. Mon expérience chez Dell en tant qu'Account Manager, où je gérais un portefeuille de 500 entreprises, a ravivé mon intérêt pour l'informatique et m'a poussé à explorer ce domaine plus en profondeur. En 2022, j'ai décidé de me consacrer pleinement à cette passion en intégrant l'école 42 pour solidifier mes bases. Aujourd'hui, je suis déterminé à mettre mes compétences et ma créativité au service de vos projets.</p>
          </Description>

          <SkillsContainer>
            <SkillsTrack>
              {doubledSkills.map((skill, index) => (
                <SkillIcon key={index}>
                  <skill.Icon />
                  <span>{skill.name}</span>
                </SkillIcon>
              ))}
            </SkillsTrack>
          </SkillsContainer>
        </ContentWrapper>
      </Container>
    </AboutSection>
  );
};

export default About;
